import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import customerActions from "../../../Actions/customerActions";
import {useDispatch, useSelector} from "react-redux";
import dateUtils from "../../../Utils/dateUtils";
import ExpandableTable from "../../Fundamentals/Tables/ExpandedableTable";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ET = ExpandableTable as any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyDispatch = any;

interface CustomerInteractionDto {
    id: number;
    interactionType: string;
    interactionDateTime: string;
    subject: string | null;
    summary: string | null;
    durationSeconds: number | null;
    aiKeyTopic: string | null;
    aiKeySubTopic: string | null;
    aiRootCause: string | null;
    sentimentCombined: string | null;
}

interface RootState {
    CustomerReducer: {
        customerInteractions: CustomerInteractionDto[] | null;
        customer: {
            orgNo?: string;
            contactPerson?: { phoneNumber?: string };
            vbCustomer?: {
                customerNumber?: string;
                mobilePhoneNumber?: string;
            };
            vossCustomer?: {
                customerNumber?: string;
                phoneNumber?: string;
            };
        };
    };
    CommonReducer: {
        dataLoading: Record<string, boolean>;
    };
}

const CustomerInteractions: React.FC = () => {
    const dispatch: AnyDispatch = useDispatch();

    const customerInteractions = useSelector((state: RootState) => state.CustomerReducer.customerInteractions);
    const customer = useSelector((state: RootState) => state.CustomerReducer.customer);
    const dataLoading = useSelector((state: RootState) => state.CommonReducer.dataLoading.customerInteractions);
    const {t} = useTranslation();

    const [fetchError, setFetchError] = useState(false);
    const fetchedForCustomerRef = useRef<string | undefined>(undefined);

    const customerNumber = customer.vbCustomer?.customerNumber ?? customer.vossCustomer?.customerNumber;
    const phoneNumber = customer.contactPerson?.phoneNumber ?? customer.vbCustomer?.mobilePhoneNumber ?? customer.vossCustomer?.phoneNumber;
    const organizationNumber = customer.orgNo;

    useEffect(() => {
        if (customerNumber === undefined) return;
        if (fetchedForCustomerRef.current === customerNumber) return;

        let cancelled = false;
        fetchedForCustomerRef.current = customerNumber;
        setFetchError(false);

        dispatch(customerActions.getCustomerInteractions(customerNumber, phoneNumber, organizationNumber))
            .catch(() => { if (!cancelled) setFetchError(true); });

        return () => { cancelled = true; };
    }, [customerNumber, phoneNumber, organizationNumber, dispatch]);

    if (dataLoading) {
        return <div className="spinner spinner-default-green m-auto d-block"/>
    }

    if (fetchError) {
        return <div className="text-center p-3">{t('customerinteractions.error')}</div>
    }

    if (customerInteractions === null || customerInteractions.length === 0) {
        return <div className="text-center p-3">{t('customerinteractions.empty')}</div>
    }

    return (
        <ET>
            <ET.Body>
                {customerInteractions.map((interaction: CustomerInteractionDto) => (
                    <ET.Row key={interaction.id}>
                        <ET.Row.Expandable>
                            <td>{interaction.aiKeySubTopic ?? t('customerinteractions.unknown')}</td>
                            <td className="text-right small">{dateUtils.formatDateAndTime(interaction.interactionDateTime)}</td>
                        </ET.Row.Expandable>
                        <ET.Row.Expanded>
                            <tr>
                                <td colSpan={2}>
                                    <div className="small" style={{whiteSpace: 'pre-wrap'}}>
                                        {interaction.summary ?? interaction.aiRootCause}
                                    </div>
                                </td>
                            </tr>
                        </ET.Row.Expanded>
                    </ET.Row>
                ))}
            </ET.Body>
        </ET>
    );
}

export default CustomerInteractions;
